import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, TrendingUp, Shield, Building2, Users, DollarSign,
  CheckCircle2, ChevronRight, BarChart3, Briefcase, Target,
  FileText, Lock, MapPin, Clock, Handshake, Award, Scale,
  Eye, LineChart, Layers, AlertTriangle, PieChart, Activity,
  Landmark, Settings, Database
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const InstitutionalPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Strategy pillars
  const strategyPillars = [
    {
      icon: Target,
      title: 'Disciplined Sourcing',
      description: 'Proprietary deal flow through established market relationships'
    },
    {
      icon: BarChart3,
      title: 'Conservative Underwriting',
      description: 'Stress-tested assumptions with margin of safety'
    },
    {
      icon: Settings,
      title: 'Operational Excellence',
      description: 'In-house management for execution control'
    },
    {
      icon: TrendingUp,
      title: 'Scalable Deployment',
      description: 'Repeatable process across multiple investments'
    }
  ];

  // Asset focus areas
  const assetFocus = [
    {
      type: 'Multifamily',
      description: 'Class B/C workforce housing with value-add potential',
      markets: ['Houston Metro', 'Katy', 'Sugar Land', 'Pearland']
    },
    {
      type: 'Commercial',
      description: 'Neighborhood retail and office with repositioning opportunity',
      markets: ['Energy Corridor', 'Westchase', 'The Woodlands']
    }
  ];

  // Alignment features
  const alignmentFeatures = [
    { icon: DollarSign, title: 'GP Co-Investment', description: 'Meaningful sponsor capital at risk in every deal' },
    { icon: Scale, title: 'Performance Alignment', description: 'Compensation tied to investor outcomes' },
    { icon: Handshake, title: 'Partnership Approach', description: 'Long-term relationships over transaction volume' },
    { icon: Eye, title: 'Full Transparency', description: 'Complete visibility into operations and holdings' }
  ];

  // Governance features
  const governanceFeatures = [
    { title: 'Quarterly Reports', description: 'Detailed property-level performance metrics and financial statements' },
    { title: 'Investor Portal', description: 'Secure 24/7 access to documents, capital account data, and reports' },
    { title: 'Compliance Standards', description: 'Rigorous adherence to regulatory requirements and best practices' },
    { title: 'Annual Reviews', description: 'Comprehensive performance review and strategy updates' }
  ];

  // Risk management pillars
  const riskPillars = [
    {
      icon: Shield,
      title: 'Conservative Leverage',
      description: 'Modest debt levels with fixed-rate financing to limit interest rate exposure'
    },
    {
      icon: Layers,
      title: 'Diversification',
      description: 'Multiple properties across submarkets to reduce concentration risk'
    },
    {
      icon: Activity,
      title: 'Stress Testing',
      description: 'Downside scenario analysis on every acquisition'
    },
    {
      icon: Database,
      title: 'Reserve Policies',
      description: 'Adequate capital reserves for market uncertainty'
    }
  ];

  // Custom structure options
  const structureOptions = [
    {
      title: 'Fund Investment',
      minimum: '$500,000',
      description: 'Commingled fund participation with diversified exposure',
      icon: Briefcase
    },
    {
      title: 'Co-Investment',
      minimum: '$1,000,000+',
      description: 'Direct participation alongside fund in select deals',
      icon: Handshake
    },
    {
      title: 'Separate Account',
      minimum: '$5,000,000+',
      description: 'Dedicated portfolio with customized strategy',
      icon: Landmark
    }
  ];

  // Track record stats
  const trackRecordStats = [
    { value: '18+', label: 'Years Operating' },
    { value: '70+', label: 'Transactions' },
    { value: '100%', label: 'Capital Returned' },
    { value: '0', label: 'Investor Losses' }
  ];

  return (
    <>
      <Helmet>
        <title>Institutional Real Estate Investment Opportunities | ASAP Capital</title>
        <meta 
          name="description" 
          content="Institutional-grade real estate investment opportunities with a disciplined value-add strategy across Houston and key Texas markets." 
        />
        <meta name="keywords" content="institutional real estate investment, private equity real estate funds, pension fund real estate, endowment investments, Texas commercial real estate" />
        <link rel="canonical" href="/investors/institutional" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#D4AF37]/5 rounded-full blur-[180px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
            
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-[0.02]">
              <div className="absolute inset-0" style={{
                backgroundImage: `linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)`,
                backgroundSize: '80px 80px'
              }} />
            </div>
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Landmark className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">For Institutional Investors</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                  variants={itemVariants}
                >
                  Real Estate Investment Opportunities for{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Institutional Investors
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-8 max-w-2xl"
                  variants={itemVariants}
                >
                  Partner with a disciplined real estate sponsor offering institutional-grade 
                  governance, transparent reporting, and proven value-add execution across 
                  Texas markets.
                </motion.p>

                {/* Key differentiators */}
                <motion.div 
                  className="flex flex-wrap gap-6 mb-10"
                  variants={itemVariants}
                >
                  <div className="flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">$500K+ Minimum</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">Institutional Governance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-white/70 text-sm">Custom Structures</span>
                  </div>
                </motion.div>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      View Fund Terms
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: VALUE-ADD INVESTMENT STRATEGY ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Disciplined Approach
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Value-Add Investment Strategy
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our institutional-grade approach combines rigorous underwriting, 
                    operational excellence, and disciplined execution across a scalable platform.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {strategyPillars.map((pillar, index) => (
                    <motion.div
                      key={pillar.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8 }}
                    >
                      <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                        <pillar.icon className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {pillar.title}
                      </h3>
                      <p className="text-white/50 text-sm">{pillar.description}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="text-center mt-10"
                  variants={itemVariants}
                >
                  <Link to="/strategy">
                    <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2 mx-auto">
                      View Full Strategy
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: MULTIFAMILY & COMMERCIAL ASSETS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Asset Focus
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Multifamily & Commercial Assets
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      We focus on workforce housing and neighborhood commercial assets in 
                      high-growth Texas submarkets. Our local expertise and established 
                      relationships enable proprietary deal flow in attractive entry points.
                    </motion.p>

                    <div className="space-y-6">
                      {assetFocus.map((asset, index) => (
                        <motion.div
                          key={asset.type}
                          className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <Building2 className="h-5 w-5 text-[#D4AF37]" />
                            <h4 className="text-white font-semibold">{asset.type}</h4>
                          </div>
                          <p className="text-white/50 text-sm mb-3">{asset.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {asset.markets.map((market) => (
                              <span 
                                key={market}
                                className="px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] text-xs"
                              >
                                {market}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.div className="mt-8" variants={itemVariants}>
                      <Link to="/portfolio">
                        <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full text-sm flex items-center gap-2">
                          View Portfolio
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>

                  {/* Stats card */}
                  <motion.div variants={fadeInUp}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-8">
                          <MapPin className="h-6 w-6 text-[#D4AF37]" />
                          <h4 className="text-white font-semibold text-lg">Houston Market Focus</h4>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <p className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">7M+</p>
                            <p className="text-white/40 text-sm mt-1">Metro Population</p>
                          </div>
                          <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <p className="text-3xl font-bold text-white">#1</p>
                            <p className="text-white/40 text-sm mt-1">Job Growth</p>
                          </div>
                          <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <p className="text-3xl font-bold text-white">$512B</p>
                            <p className="text-white/40 text-sm mt-1">Regional GDP</p>
                          </div>
                          <div className="text-center p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <p className="text-3xl font-bold text-emerald-400">15+</p>
                            <p className="text-white/40 text-sm mt-1">Years Local</p>
                          </div>
                        </div>

                        <Link to="/why-houston" className="block mt-6">
                          <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-xl text-sm">
                            Explore Houston Market
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: ALIGNED INTERESTS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Visual */}
                  <motion.div variants={fadeInUp} className="order-2 lg:order-1">
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10">
                        <h4 className="text-white font-semibold mb-6">GP/LP Alignment Structure</h4>
                        
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">GP Co-Investment</span>
                            <span className="text-[#D4AF37] font-semibold">Every Deal</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Preferred Return</span>
                            <span className="text-white font-semibold">8%</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Carried Interest</span>
                            <span className="text-white font-semibold">20% above pref</span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-[#0A1628]/50 rounded-xl border border-white/5">
                            <span className="text-white/60 text-sm">Management Fee</span>
                            <span className="text-white font-semibold">1.5%</span>
                          </div>
                        </div>

                        <Link to="/fund-terms" className="block mt-6">
                          <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold py-3 h-11 rounded-xl text-sm">
                            View Complete Terms
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <div className="order-1 lg:order-2">
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      True Partnership
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Aligned Interests
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Our compensation structure ensures meaningful alignment with 
                      institutional partners. We invest our own capital alongside LPs 
                      and earn carried interest only after delivering the preferred return.
                    </motion.p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {alignmentFeatures.map((feature, index) => (
                        <motion.div
                          key={feature.title}
                          className="flex items-start gap-3"
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <feature.icon className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
                            <p className="text-white/40 text-xs mt-1">{feature.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: GOVERNANCE & REPORTING ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Institutional Standards
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Governance & Reporting
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    We maintain rigorous reporting standards and governance practices 
                    designed to meet institutional due diligence requirements.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {governanceFeatures.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/20 transition-colors"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                        <span className="text-[#D4AF37] font-bold">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                      <p className="text-white/50 text-sm">{feature.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: RISK MANAGEMENT ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Capital Protection
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Risk Management
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      Capital preservation is foundational to our investment approach. 
                      We employ multiple risk mitigation strategies to protect principal 
                      while pursuing attractive risk-adjusted returns.
                    </motion.p>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {riskPillars.map((pillar, index) => (
                        <motion.div
                          key={pillar.title}
                          className="bg-[#111D35]/50 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/20 transition-colors"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                              <pillar.icon className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <h4 className="text-white font-semibold text-sm">{pillar.title}</h4>
                              <p className="text-white/40 text-xs mt-1">{pillar.description}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Visual */}
                  <motion.div variants={fadeInUp}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute -top-12 -right-12 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10 text-center">
                        <div className="w-20 h-20 mx-auto rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 border border-emerald-500/20">
                          <Shield className="h-10 w-10 text-emerald-400" />
                        </div>
                        <h4 className="text-2xl font-bold text-white mb-2">Zero Investor Losses</h4>
                        <p className="text-white/50 text-sm mb-6">
                          Track record of capital preservation across 18+ years
                        </p>
                        
                        <div className="pt-6 border-t border-white/10">
                          <div className="text-4xl font-bold text-emerald-400 mb-2">100%</div>
                          <p className="text-white/40 text-sm">Capital Returned to Investors</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: CUSTOM STRUCTURES ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    Flexible Structures
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    Custom Structures
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    We offer flexible investment structures to accommodate institutional 
                    requirements and larger capital allocations.
                  </motion.p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {structureOptions.map((option, index) => (
                    <motion.div
                      key={option.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 text-center group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors border border-[#D4AF37]/20">
                        <option.icon className="h-8 w-8 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {option.title}
                      </h3>
                      <p className="text-[#D4AF37] font-semibold text-lg mb-3">{option.minimum}</p>
                      <p className="text-white/50 text-sm">{option.description}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.p 
                  className="text-white/40 text-xs text-center mt-8 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  Custom structures may be available for qualified institutional investors. 
                  Contact us to discuss specific allocation requirements.
                </motion.p>
              </motion.div>
            </div>
          </section>

          {/* ==================== H2: PROVEN TRACK RECORD ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="text-center mb-12">
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Demonstrated Results
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Proven Track Record
                    </motion.h2>
                    <motion.p 
                      className="text-white/50 max-w-2xl mx-auto"
                      variants={itemVariants}
                    >
                      Our performance across multiple market cycles demonstrates disciplined 
                      execution and consistent value creation.
                    </motion.p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {trackRecordStats.map((stat, index) => (
                      <motion.div
                        key={stat.label}
                        className="text-center p-6 bg-[#0A1628]/50 rounded-xl border border-white/5"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <p className="text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent mb-2">
                          {stat.value}
                        </p>
                        <p className="text-white/50 text-sm">{stat.label}</p>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div 
                    className="flex flex-wrap justify-center gap-4"
                    variants={itemVariants}
                  >
                    <Link to="/track-record">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-11 rounded-full text-sm flex items-center gap-2">
                        View Track Record
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full text-sm">
                        Case Studies
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FINAL CTA ==================== */}
          <section className="py-24 lg:py-32 relative bg-[#111D35]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d1a2d] via-transparent to-[#0d1a2d]" />
            
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.span 
                  className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                  variants={itemVariants}
                >
                  Begin Due Diligence
                </motion.span>
                <motion.h2 
                  className="text-2xl lg:text-4xl font-bold text-white mb-5"
                  variants={itemVariants}
                >
                  Partner With ASAP Capital
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-10"
                  variants={itemVariants}
                >
                  Schedule a consultation to discuss how our institutional-grade real estate 
                  platform can support your investment objectives.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-14 rounded-full text-base">
                      Schedule Consultation
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-14 rounded-full text-base">
                      Request Materials
                    </Button>
                  </Link>
                </motion.div>

                {/* Compliance note */}
                <motion.p 
                  className="text-white/30 text-xs mt-10 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  ASAP Capital investments are available exclusively to qualified institutional 
                  investors and accredited investors. All investments involve risk including 
                  potential loss of principal. Please refer to the Private Placement Memorandum 
                  for complete terms and disclosures.
                </motion.p>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default InstitutionalPage;
